// src/pages/Community/LocationIndex.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/QuickPanel.css";
import "./css/LocationIndex.css";
import ToolbarHeader, { GTA_GAME_TABS } from "./components/ToolbarHeader";
import Modal from "./components/Modal";
import LocationForm from "./Location/LocationForm";
import EmptyState from "./components/EmptyState";
import DetailSidePanel from "./components/DetailSidePanel";
import LocationDetailPanel from "./Location/LocationDetailPanel";

export const LOCATION_CATEGORIES = [
  "Hidden Location",
  "Secret Spots",
  "Collectibles",
  "Stunt Spots",
  "Safehouses",
  "Shops & Garages",
  "Landmarks",
  "Other",
];

export default function LocationIndex({ user }) {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState("gta-5");
  const [showModal, setShowModal] = useState(false);
  const [category, setCategory] = useState("all");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const navigate = useNavigate();
  /* =========================
     ROLE LOGIC (same as Missions)
     ========================= */
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin";
  const canAddLocation = isAdmin;

  const isMine = (loc) => {
    const uid = user?.uid;
    return (
      !!uid &&
      [loc.authorUid, loc.createdBy, loc.createdByUid, loc.ownerId].some(
        (x) => x === uid
      )
    );
  };

  /* =========================
     FIRESTORE LISTENER
     ========================= */
  useEffect(() => {
    setLoading(true);

    const base = collection(db, "locations");
    let q;

    if (selectedKey === "mine" && isAdmin && user?.uid) {
      // My locations only
      q = query(
        base,
        where("authorUid", "==", user.uid),
        orderBy("createdAt", "desc")
      );
    } else {
      // EXACTLY like MissionIndex / StoryCharacterIndex
      q = query(
        base,
        where("isPrivate", "==", false)
      );
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLocations(docs);
        setLoading(false);
      },
      (err) => {
        console.error("locations query error:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [selectedKey, user?.uid, isAdmin]);


  /* =========================
     CLIENT FILTERING
     ========================= */
  const norm = (s) => (s || "").toLowerCase().trim();

  const list = useMemo(() => {
    let arr = [...locations];

    // 🔒 private handling
    arr = arr.filter((l) => {
      const isPrivate = Boolean(l.isPrivate ?? l.isHidden);
      return !isPrivate || isMine(l);
    });

    // 🎮 game filter
    if (selectedKey !== "all" && selectedKey !== "mine") {
      arr = arr.filter((l) => l.gameKey === selectedKey);
    }

    // 📂 category filter
    if (category !== "all") {
      arr = arr.filter((l) => norm(l.category) === norm(category));
    }

    // 🔍 search
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((l) =>
        [
          l.title,
          l.city,
          l.region,
          l.shortNote,
          l.category,
          ...(l.tags || []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    return arr;
  }, [locations, search, selectedKey, category, user?.uid]);

  /* =========================
     RENDER
     ========================= */
  return (
    <>
      <ToolbarHeader
        user={user}
        search={search}
        setSearch={setSearch}
        placeholder="Search locations, city, tags…"
        onAddMission={canAddLocation ? () => setShowModal(true) : undefined}
        addLabel={canAddLocation ? "＋ Add Location" : null}
      >
        {/* 🎮 GAME FILTER (same as now) */}
        <div className="forum-tabs forum-tabs--games">
          {GTA_GAME_TABS
            .concat(isAdmin ? [{ key: "mine", label: "My Locations" }] : [])
            .map(({ key, label }) => (
              <span
                key={key}
                className={`tab-text ${selectedKey === key ? "active" : ""}`}
                onClick={() => setSelectedKey(key)}
              >
                {label}
              </span>
            ))}
        </div>

        {/* 📂 LOCATION CATEGORY FILTER */}
        <div className="forum-tabs forum-tabs--categories">
          <span
            className={`tab-text ${category === "all" ? "active" : ""}`}
            onClick={() => setCategory("all")}
          >
            All Categories
          </span>

          {LOCATION_CATEGORIES.map((c) => (
            <span
              key={c}
              className={`tab-text ${category === c ? "active" : ""}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </span>
          ))}
        </div>
      </ToolbarHeader>

      <div className={`loc-shell ${selectedLocationId ? "panel-open" : ""}`}>
        <div className="loci-wrap">


          <ul className="loci-grid">
            {loading && (
              <li className="empty-block">
                Loading…
              </li>
            )}
            {!loading &&
              list.map((loc) => {
                const gameLabel =
                  GTA_GAME_TABS.find((g) => g.key === loc.gameKey)?.label ||
                  "Location";

                const isOwner = isMine(loc);
                const isPrivate = Boolean(loc.isPrivate ?? loc.isHidden);

                return (
                  <li key={loc.id} className="loci-card-btn"
                    onClick={() => setSelectedLocationId(loc.id)}>
                    {isOwner && <span className="loci-owner">👑</span>}

                    <div className="loci-img">
                      {loc.gameMode === "online" && (
                        <span
                          className="loci-mode-icon"
                          title="Online"
                          aria-label="Online"
                        >
                          🌐
                        </span>
                      )}


                      {loc.coverUrl ? (
                        <img src={loc.coverUrl} alt="" loading="lazy" />
                      ) : (
                        <div className="loci-img--placeholder">🗺️</div>
                      )}
                    </div>


                    <div className="loci-card-body">
                      <div className="loci-row">
                        <span className="loci-badge">{gameLabel}</span>
                        {isOwner && (
                          <span
                            className={`loci-tag ${isPrivate ? "is-hidden" : "is-visible"}`}
                            title="Visibility (only visible to you)"
                          >
                            {isPrivate ? "Private" : "Public"}
                          </span>
                        )}

                      </div>

                      <h3>{loc.title || "Untitled location"}</h3>

                      <div className="loci-meta">
                        {[loc.city, loc.region].filter(Boolean).join(" • ")}
                      </div>
                    </div>
                  </li>
                );
              })}

            {!loading && list.length === 0 && (
              <li className="empty-block">
                <EmptyState
                  icon="🗺️"
                  title="No Locations Available"
                  description="Please stay tuned. New locations will be added soon."
                />
              </li>
            )}

          </ul>
        </div>
        {/* RIGHT: QUICK PANEL */}
        <DetailSidePanel
          open={!!selectedLocationId}
          title="Location Details"
          onClose={() => setSelectedLocationId(null)}
          onMaximize={() => navigate(`/map-locations/${selectedLocationId}`)}
        >
          <LocationDetailPanel
            locationId={selectedLocationId}
            user={user}
          />
        </DetailSidePanel>


        {/* ADMIN-ONLY MODAL */}
        {canAddLocation && (
          <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            title="Add a Location"
          >
            <LocationForm
              user={user}
              onDone={() => setShowModal(false)}
              onCancel={() => setShowModal(false)}
            />
          </Modal>
        )}
      </div >
    </>
  );
}
